import {
    Box,
    CircularProgress,
    Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import TreffasButton from "src/components/TreffasButton";
import { useLocation, useNavigate } from "react-router-dom";

import http from "src/utils/http";
import Logo from "src/components/Logo";
import SideBg from "src/views/welcome/SideBg";
import { Users } from "src/context";
import Page from "src/components/Page";
import Pendo from "src/utils/Pendo";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const MagicLinkVerify = () => {
    const navigate = useNavigate();
    const searchParams = useQuery();
    const email = searchParams.get("email");
    const token = searchParams.get("token");
    const { mixpanel } = useContext(Users);
    const [isProcess, setIsProcess] = useState(false);
    const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', 'expired', 'invalid'
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        mixpanel.track("Magic Link Verify", {
            location: window.location.href,
        });

        // Auto-verify when component mounts if email and token are present
        if (email && token) {
            verifyMagicLink();
        } else {
            setVerificationStatus("error");
            setErrorMessage("Missing email or token in the link.");
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const verifyMagicLink = () => {
        if (!email || !token) {
            setVerificationStatus("error");
            setErrorMessage("Missing email or token in the link.");
            return;
        }

        setIsProcess(true);
        setVerificationStatus(null);
        setErrorMessage("");

        http.post("/api/oauth/magic-link/verify", {
            email: email,
            token: token,
        })
            .then(({ data }) => {
                if (data.message === "success") {
                    setVerificationStatus("success");

                    // Initialize pendo
                    Pendo.initialize(data.data);

                    if (data.two_factor_enable) {
                        navigate(
                            `/two-factor/verification?token=${data.access_token}&id=${data?.data?.id}&organization=${data.data.organization.id}&mask=${data.mask}&type=${data.two_factor_type}`
                        );
                    } else {
                        localStorage.setItem(`accessToken`, data.access_token);
                        localStorage.setItem(`__loginType`, "coach");
                        window.location.href = `/app/v2/calendar`;
                    }
                }

                if (data.message === "email-not-found") {
                    setVerificationStatus("error");
                    setErrorMessage("Email address not found.");
                }

                if (data.message === "account-unverified") {
                    setVerificationStatus("error");
                    setErrorMessage("Account is not verified. Please verify your email first.");
                }

                if (data.message === "token-invalid") {
                    setVerificationStatus("error");
                    setErrorMessage("Invalid magic link. The link may have been used already or is invalid.");
                }

                if (data.message === "token-expired") {
                    setVerificationStatus("expired");
                    setErrorMessage("This magic link has expired. Please request a new one.");
                }

                if (data.message === "organization-not-found") {
                    setVerificationStatus("error");
                    setErrorMessage("Organization not found.");
                }
            })
            .catch((err) => {
                console.log(err.message);
                setVerificationStatus("error");
                setErrorMessage("Failed to verify magic link. Please try again.");
            })
            .finally(() => setIsProcess(false));
    };

    return (
        <Page title="Verify Magic Link">
            <Box
                sx={(themes) => ({
                    height: "80px",
                    alignItems: "center",
                    [themes.breakpoints.up("md")]: {
                        alignItems: "center",
                        boxShadow: "0px 2px 2px rgba(0, 0, 0, 0.04)",
                        pl: "99px",
                        display: "none",
                    },
                    [themes.breakpoints.down("md")]: {
                        justifyContent: "flex-start",
                        pl: "24px !important",
                        display: "flex",
                    },
                })}
            >
                <Logo />
            </Box>

            <Box
                sx={{
                    minHeight: { xs: "calc(100vh - 120px)", md: "calc(100vh - 0px)" },
                    justifyContent: "space-between",
                    display: "flex",
                }}
            >
                <Box
                    sx={{
                        pt: {
                            md: "120px",
                        },
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: "100%", md: "440px" },
                        }}
                    >
                        <Box sx={{ mt: "24px", textAlign: "center" }}>
                            {isProcess && !verificationStatus && (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <CircularProgress size={20} color="inherit" />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "16px",
                                            lineHeight: "24px",
                                            fontWeight: 500,
                                            color: "#19181B",
                                            mb: 1,
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        Verifying magic link...
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            color: "#6E757C",
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        Please wait while we verify your magic link.
                                    </Typography>
                                </>
                            )}

                            {verificationStatus === "success" && (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <CheckCircle
                                            sx={{
                                                fontSize: "50px",
                                                color: "#4CAF50",
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            lineHeight: "24px",
                                            fontWeight: 500,
                                            color: "#19181B",
                                            mb: 1,
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        Magic link verified!
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            color: "#6E757C",
                                            mb: 3,
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        Redirecting you to your dashboard...
                                    </Typography>
                                </>
                            )}

                            {(verificationStatus === "error" || verificationStatus === "expired") && (
                                <>
                                    <Box sx={{ mb: 2 }}>
                                        <ErrorIcon
                                            sx={{
                                                fontSize: "50px",
                                                color: "#F44336",
                                            }}
                                        />
                                    </Box>
                                    <Typography
                                        sx={{
                                            fontSize: "18px",
                                            lineHeight: "24px",
                                            fontWeight: 500,
                                            color: "#19181B",
                                            mb: 1,
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        {verificationStatus === "expired" ? "Link Expired" : "Verification Failed"}
                                    </Typography>

                                    <Typography
                                        sx={{
                                            fontSize: "14px",
                                            lineHeight: "20px",
                                            color: "#6E757C",
                                            mb: 3,
                                            fontFamily: "var(--font-work-sans)",
                                        }}
                                    >
                                        {errorMessage}
                                    </Typography>

                                    <TreffasButton
                                        variant="primary"
                                        size="md"
                                        onClick={() => navigate("/login?form=magic-link")}
                                        sx={{ minWidth: "200px" }}
                                    >
                                        Back to Login
                                    </TreffasButton>
                                </>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        overflow: "hidden",
                        width: "1100px",
                        display: { xs: "none", lg: "block" },
                    }}
                >
                    <SideBg />
                </Box>
            </Box>
        </Page>
    );
};

export default MagicLinkVerify;
